import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  createWorkspace as createWorkspaceApi,
  fetchCurrentAccount,
  fetchCurrentUser,
  loginUser,
} from '../api/users';
import {
  clearStoredAccountId,
  clearStoredApiKey,
  getStoredAccountId,
  getStoredApiKey,
  setStoredAccountId,
  setStoredApiKey,
} from '../utils/api';
import { isAdmin } from '../support/accountFunction';

const AuthContext = createContext(null);

function resolveCurrentMembership(memberships, accountId) {
  if (!memberships?.length) {
    return null;
  }
  if (accountId) {
    return memberships.find((item) => item.account_id === accountId) || null;
  }
  if (memberships.length === 1) {
    return memberships[0];
  }
  return null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [account, setAccount] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [currentMembership, setCurrentMembership] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const loadAccountProfile = useCallback(async (membership) => {
    if (!membership) {
      setAccount(null);
      return null;
    }

    setStoredAccountId(membership.account_id);
    setCurrentMembership(membership);

    try {
      const accountProfile = await fetchCurrentAccount();
      const nextAccount = {
        id: accountProfile.id,
        name: accountProfile.name,
        is_active: accountProfile.is_active,
        created_at: accountProfile.created_at,
      };
      setAccount(nextAccount);
      return nextAccount;
    } catch {
      const fallback = membership.account || null;
      setAccount(fallback);
      return fallback;
    }
  }, []);

  const applySession = useCallback(
    async ({ userData, membershipsData, selectedMembership = null }) => {
      setUser(userData);
      setMemberships(membershipsData || []);
      setIsAuthenticated(true);

      if (isAdmin(userData)) {
        clearStoredAccountId();
        setCurrentMembership(null);
        setAccount(null);
        return;
      }

      await loadAccountProfile(selectedMembership);
    },
    [loadAccountProfile],
  );

  const clearSession = useCallback(() => {
    clearStoredApiKey();
    clearStoredAccountId();
    setUser(null);
    setAccount(null);
    setMemberships([]);
    setCurrentMembership(null);
    setIsAuthenticated(false);
  }, []);

  const validateStoredKey = useCallback(async () => {
    const apiKey = getStoredApiKey();
    if (!apiKey) {
      setIsLoading(false);
      return;
    }

    try {
      const profile = await fetchCurrentUser();
      const selectedMembership = isAdmin(profile.user)
        ? null
        : resolveCurrentMembership(profile.memberships, getStoredAccountId());

      await applySession({
        userData: profile.user,
        membershipsData: profile.memberships,
        selectedMembership,
      });
    } catch {
      clearSession();
    } finally {
      setIsLoading(false);
    }
  }, [applySession, clearSession]);

  useEffect(() => {
    validateStoredKey();
  }, [validateStoredKey]);

  const login = async (email, password) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      throw new Error('Informe e-mail e senha');
    }

    const { api_key: apiKey, user: userData, memberships: membershipsData } = await loginUser({
      email: trimmedEmail,
      password,
    });

    setStoredApiKey(apiKey);

    const selectedMembership = isAdmin(userData)
      ? null
      : resolveCurrentMembership(membershipsData, getStoredAccountId())
        || (membershipsData.length === 1 ? membershipsData[0] : null);

    await applySession({
      userData,
      membershipsData,
      selectedMembership,
    });

    return {
      user: userData,
      memberships: membershipsData,
      account: selectedMembership?.account || null,
      needsAccountSelection: !isAdmin(userData) && membershipsData.length > 1 && !selectedMembership,
      needsWorkspaceCreation: !isAdmin(userData) && membershipsData.length === 0,
    };
  };

  const createWorkspace = useCallback(
    async (name) => {
      const trimmedName = name.trim();
      if (!trimmedName) {
        throw new Error('Informe o nome do workspace');
      }

      const { account: createdAccount, membership } = await createWorkspaceApi({
        name: trimmedName,
      });

      const nextMemberships = [...memberships, membership];
      setMemberships(nextMemberships);
      await loadAccountProfile(membership);

      return createdAccount;
    },
    [loadAccountProfile, memberships],
  );

  const switchAccount = useCallback(
    async (accountId) => {
      const membership = resolveCurrentMembership(memberships, accountId);
      if (!membership) {
        throw new Error('Workspace não encontrado');
      }
      await loadAccountProfile(membership);
    },
    [loadAccountProfile, memberships],
  );

  const logout = () => {
    clearSession();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        account,
        memberships,
        currentMembership,
        isAuthenticated,
        isLoading,
        login,
        logout,
        switchAccount,
        createWorkspace,
        refreshProfile: validateStoredKey,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
